import { useAddNewHireContacts } from '@features/digitalOnboarding/hooks/useAddNewHireContacts';
import { NewHireConacts, PayrollPartners } from '@features/digitalOnboarding/types';
import { yupResolver } from '@hookform/resolvers/yup';
import DoneIcon from '@mui/icons-material/Done';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FC, useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import CloseIcon from '@mui/icons-material/Close';
import { EditIcon, AddPlusIcon } from '../../../../vectors/index';
import { useUpdateNewHireContact } from '@features/digitalOnboarding/hooks/useUpdateNewHireContact';
import { getStrippedText } from '@utils/helper';

type AddOnboardingPackageModalProps = {
  isOpen: boolean;
  companyId?: number;
  selectedCompanyId?: number | null;
  handleClose: () => void;
  newHireNotificationData: NewHireConacts | null;
};

type FormData = {
  email: string;
  name: string;
  company_name: string;
};

const schema: yup.ObjectSchema<FormData> = yup.object({
  name: yup.string().required('Name is required').test('not-empty-html', 'Name cannot be empty', (value) => {
    if (!value) return false;
    return getStrippedText(value).length > 0;
  }).default(''), 
  company_name: yup.string().required('Company name is required').test('not-empty-html', 'Company Name cannot be empty', (value) => {
    if (!value) return false;
    return getStrippedText(value).length > 0;
  }).default(''), 
  email: yup.string().email('Invalid email').required('Email is required'), 
});

const NewHireNotificationModal: FC<AddOnboardingPackageModalProps> = ({
  isOpen,
  selectedCompanyId,
  companyId,
  handleClose,
  newHireNotificationData,
}) => {
  const [errorText, setErrorText] = useState<string>('');
  const [defaultValues, setDefaultValues] = useState<FormData>({
    email: '',
    name: '',
    company_name: '',
  });


  useEffect(() => {
    if (newHireNotificationData) {
      setDefaultValues({
        email: newHireNotificationData.email || '',
        name: newHireNotificationData.name || '',
        company_name: newHireNotificationData.company_name || '',
      });
    } else {
      setDefaultValues({
        email: '',
        name: '',
        company_name: '',
      });
    }
  }, [newHireNotificationData]);

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    mode: 'onTouched',
    resolver: yupResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const { addNewHireContact, isAddingNewHireContact } = useAddNewHireContacts({
    setErrorText,
  });
  const { updateNewHireContact, isUpdatingNewHireContact } =
    useUpdateNewHireContact({
      setErrorText,
    });

  const onSubmit = (values: FormData) => {
    const options = {
      onSuccess: (data: {
        status: number; message: string
      }) => {
        if (data.status === 200) {
          toast.success(data.message);
          reset();
          handleClose();
        }
      },
    };
    if (newHireNotificationData) {
      updateNewHireContact(
        {
          ...values,
          ...(selectedCompanyId ? { company_id: selectedCompanyId } : {}),
          id: newHireNotificationData.id,
        },
        options,
      );
    } else {
      addNewHireContact({ ...values, ...(companyId ? { company_id: companyId } : {}), }, options);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        reset();
        handleClose();
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />
        {newHireNotificationData ? <EditIcon /> : <AddPlusIcon />}
        <br />
        <Typography variant="h4" mt={1}>
          {newHireNotificationData ? 'Update' : 'Add'} New Hire Contact
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ marginBottom: 3 }}>
          {errorText && <Alert severity="error">{errorText}</Alert>}
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack
              spacing={3}
              sx={{
                pt: 1,
                '& p': {
                  ml: 1,
                  fontSize: '15px',
                },
              }}
            >
              <Controller
                control={control}
                name="email"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={Boolean(errors.email)}
                    helperText={errors?.email ? errors?.email.message : ''}
                  />
                )}
              />

              <Controller
                control={control}
                name="name"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Name"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={Boolean(errors.name)}
                    helperText={errors?.name ? errors?.name.message : ''}
                  />
                )}
              />

              <Controller
                control={control}
                name="company_name"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Company Name"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={Boolean(errors.company_name)}
                    helperText={
                      errors?.company_name ? errors?.company_name.message : ''
                    }
                  />
                )}
              />
              <LoadingButton
                type="submit"
                startIcon={<DoneIcon />}
                variant="contained"
                size="small"
                fullWidth
                loading={isAddingNewHireContact || isUpdatingNewHireContact}
                disabled={isAddingNewHireContact || isUpdatingNewHireContact}
                loadingPosition="start"
              >
                Save New Hire Contact
              </LoadingButton>
            </Stack>
          </form>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default NewHireNotificationModal;
